import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { container, TYPES } from '../../../shared/services/inversify.config';
import type { ICustomerService } from '../../../shared/services/interface/ICustomerService';
import type { Customer, CustomerResponse } from '../../../shared/models/customerModel';
import { enrichCustomersForDashboard } from '../utils/onboardingDashboardUtils';

interface CustomerContextType {
  customers: Customer[];
  totalItemCount: number;
  loading: boolean;
  error: string | null;
  refetch: (pageNumber?: number, pageSize?: number, view?: 'board' | 'table') => Promise<void>;
  getCustomerById: (id: string) => Customer | undefined;
}

const CustomerContext = createContext<CustomerContextType | undefined>(undefined);

interface CustomerProviderProps {
  children: ReactNode;
  refreshInterval?: number;
  initialPageNumber?: number;
  initialPageSize?: number;
}

export const CustomerProvider: React.FC<CustomerProviderProps> = ({
  children,
  refreshInterval = 0, // Disabled auto-refresh
  initialPageNumber = 1,
  initialPageSize = 10,
}) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [totalItemCount, setTotalItemCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(initialPageNumber);
  const [pageSize, setPageSize] = useState<number>(initialPageSize);
  const [currentView, setCurrentView] = useState<'board' | 'table'>('board'); // Track current view

  // Get service instance (won't throw during render)
  const customerService = React.useMemo(() => {
    try {
      const service = container.get<ICustomerService>(TYPES.ICustomerService);
      return service;
    } catch (err) {
      console.error('Failed to initialize CustomerService:', err);
      setError('Failed to initialize CustomerService: ' + (err instanceof Error ? err.message : String(err)));
      setLoading(false);
      return null;
    }
  }, []);

  const fetchCustomers = useCallback(async (page?: number, size?: number, view?: 'board' | 'table') => {
    // Don't fetch if service failed to initialize
    if (!customerService) {
      console.error('Cannot fetch customers: service not initialized');
      return;
    }

    try {
      setError(null);
      const currentPage = page ?? pageNumber;
      const currentSize = size ?? pageSize;
      const viewToUse = view ?? currentView; // Use current view if not specified

      let data: CustomerResponse;
      if (viewToUse === 'board') {
        // Use board view API - no pagination
        data = await customerService.GetAllCustomersBoardView();
        
        // Handle if API returns array directly instead of {customers, totalItemCount}
        if (Array.isArray(data)) {
          data = {
            customers: data,
            totalItemCount: data.length
          };
        }
      } else {
        // Use table view API - with pagination
        data = await customerService.GetAllCustomers(currentPage, currentSize);
        
        // Handle if API returns array directly instead of {customers, totalItemCount}
        if (Array.isArray(data)) {
          data = {
            customers: data,
            totalItemCount: data.length
          };
        }
      }
      
      setCustomers(enrichCustomersForDashboard(data.customers));
      setTotalItemCount(data.totalItemCount);

      if (page !== undefined) setPageNumber(page);
      if (size !== undefined) setPageSize(size);
      if (view !== undefined) setCurrentView(view);
    } catch (err) {
      console.error('Error fetching customers:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch customers';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [customerService, pageNumber, pageSize, currentView]);

  const refetch = useCallback(async (page?: number, size?: number, view?: 'board' | 'table') => {
    setLoading(true);
    await fetchCustomers(page, size, view);
  }, [fetchCustomers]);

  const getCustomerById = useCallback((id: string): Customer | undefined => {
    return customers.find(customer => customer.id === id);
  }, [customers]);

  // Initial fetch
  useEffect(() => {
    try {
      fetchCustomers(undefined, undefined, 'board'); // Start with board view
    } catch (err) {
      console.error('CustomerContext: Error in initial fetch:', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize customer data');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set up interval for real-time updates
  useEffect(() => {
    if (refreshInterval > 0) {
      const interval = setInterval(() => {
        fetchCustomers(undefined, undefined, currentView); // Use current view for auto-refresh
      }, refreshInterval);

      return () => clearInterval(interval);
    }
  }, [refreshInterval, currentView, fetchCustomers]);

  const value: CustomerContextType = {
    customers,
    totalItemCount,
    loading,
    error,
    refetch,
    getCustomerById,
  };

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
};

export const useCustomerContext = (): CustomerContextType => {
  const context = useContext(CustomerContext);
  if (context === undefined) {
    throw new Error('useCustomerContext must be used within a CustomerProvider');
  }
  return context;
};